import {
  ForwardRefRenderFunction,
  KeyboardEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Customer, createCustomer, fetchCustomers } from '../../../api/customers';
import {
  CustomerVehicle,
  createCustomerVehicle,
  fetchCustomerVehicles,
} from '../../../api/customerVehicles';
import CustomerVehicleModal from '../../../components/CustomerVehicleModal';
import AddCustomerModal from '../../../components/AddCustomerModal';
import type { UserProfile } from '../../../api/auth';
import { CreateOrderPayload, createOrder } from '../../../api/orders';
import { fetchLocations, Location } from '../../../api/locations';

type VehicleModalField =
  | 'vehicleBrandId'
  | 'vehicleModelId'
  | 'vehicleRegistrationYear'
  | 'vehicleRegistrationNumber'
  | 'vehicleVin'
  | 'vehicleChassisNumber'
  | 'fuelType';

interface VehicleModalState {
  vehicleBrandId: string;
  vehicleModelId: string;
  vehicleRegistrationYear: string;
  vehicleRegistrationNumber: string;
  vehicleVin: string;
  vehicleChassisNumber: string;
  fuelType: string[];
}

export interface CustomerVehicleSelectionSnapshot {
  customer: Customer | null;
  customerVehicleId: string;
  vehicleBrandId: string;
  vehicleModelId: string;
  vehicleRegistrationNumber: string;
  vehicleRegistrationYear: string;
  vehicleVin: string;
  vehicleChassisNumber: string;
  locationId?: string;
}

export interface CustomerVehicleSelectionHandle {
  validate: () => string[];
  getDetails: () => CustomerVehicleSelectionSnapshot;
  setDetails: (snapshot: CustomerVehicleSelectionSnapshot) => void;
}

interface CustomerVehicleSelectionProps {
  onChange?: (snapshot: CustomerVehicleSelectionSnapshot) => void;
  currentUser: UserProfile | null;
  loadingUser: boolean;
  orderId?: string | null;
  defaultSnapshot?: CustomerVehicleSelectionSnapshot | null;
  showProceedButton?: boolean;
}

export const createEmptyCustomerVehicleSnapshot = (): CustomerVehicleSelectionSnapshot => ({
  customer: null,
  customerVehicleId: '',
  vehicleBrandId: '',
  vehicleModelId: '',
  vehicleRegistrationNumber: '',
  vehicleRegistrationYear: '',
  vehicleVin: '',
  vehicleChassisNumber: '',
  locationId: '',
});

const CustomerVehicleSelectionComponent: ForwardRefRenderFunction<
  CustomerVehicleSelectionHandle,
  CustomerVehicleSelectionProps
> = (
  {
    onChange,
    currentUser,
    loadingUser,
    orderId,
    defaultSnapshot,
    showProceedButton,
  },
  ref,
) => {
    const navigate = useNavigate();
    const createVehicleModalState = useCallback((): VehicleModalState => ({
      vehicleBrandId: '',
      vehicleModelId: '',
      vehicleRegistrationYear: '',
      vehicleRegistrationNumber: '',
      vehicleVin: '',
      vehicleChassisNumber: '',
      fuelType: [],
    }), []);

    const normalizeYearInput = useCallback((value: string): number | null => {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }
      const numeric = Number(trimmed);
      if (Number.isNaN(numeric)) {
        return null;
      }
      return Math.trunc(numeric);
    }, []);

    const isEditingExistingOrder = Boolean(orderId);

    const [searchTerm, setSearchTerm] = useState('');
    const [lastQuery, setLastQuery] = useState<string | null>(null);
    const [customerResults, setCustomerResults] = useState<Customer[]>([]);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [customerError, setCustomerError] = useState<string | null>(null);
    const [details, setDetails] = useState<CustomerVehicleSelectionSnapshot>(createEmptyCustomerVehicleSnapshot);
    const [hasSearched, setHasSearched] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [customerVehicles, setCustomerVehicles] = useState<CustomerVehicle[]>([]);
    const [vehicleLoading, setVehicleLoading] = useState(false);
    const [vehicleError, setVehicleError] = useState<string | null>(null);
    const [selectedVehicleId, setSelectedVehicleId] = useState('');
    const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
    const [vehicleModalSaving, setVehicleModalSaving] = useState(false);
    const [vehicleModalError, setVehicleModalError] = useState<string | null>(null);
    const [vehicleModalState, setVehicleModalState] = useState<VehicleModalState | null>(null);
    const [editingVehicleId, setEditingVehicleId] = useState<string | null>(null);
    const [customerModalOpen, setCustomerModalOpen] = useState(false);
    const [formErrors, setFormErrors] = useState<string[]>([]);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isProceeding, setIsProceeding] = useState(false);
    const [locations, setLocations] = useState<Location[]>([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [locationsError, setLocationsError] = useState<string | null>(null);
    const [selectedLocationId, setSelectedLocationId] = useState<string>('');

    useEffect(() => {
      let active = true;
      setLocationsLoading(true);
      setLocationsError(null);
      fetchLocations({ page: 1, limit: 200 })
        .then((resp) => {
          if (!active) return;
          const raw = resp.data || [];
          const userLocs = currentUser?.locations;
          let allowed = raw;
          if (Array.isArray(userLocs) && userLocs.length > 0) {
            const allowedErp = new Set(userLocs.map((l) => l.id));
            allowed = raw.filter((l) => allowedErp.has(l.erpId));
          }
          setLocations(allowed);
        })
        .catch((err) => {
          if (active) setLocationsError(err instanceof Error ? err.message : 'Failed to load locations.');
        })
        .finally(() => { if (active) setLocationsLoading(false); });
      return () => { active = false; };
    }, [currentUser?.id]);

    // If nothing selected, default to the first available location once loaded
    useEffect(() => {
      if (!locationsLoading && !locationsError && selectedLocationId === '' && locations.length > 0) {
        setSelectedLocationId(locations[0].id);
      }
    }, [locationsLoading, locationsError, locations, selectedLocationId]);

    const applyVehicleToDetails = useCallback(
      (vehicle: CustomerVehicle) => {
        setDetails((prev) => ({
          ...prev,
          customerVehicleId: vehicle.id ?? '',
          vehicleBrandId: vehicle.vehicleBrandId ?? '',
          vehicleModelId: vehicle.vehicleModelId ?? '',
          vehicleRegistrationYear: vehicle.vehicleRegistrationYear?.toString() ?? '',
          vehicleRegistrationNumber: vehicle.vehicleRegistrationNumber ?? '',
          vehicleVin: vehicle.vehicleVin ?? '',
          vehicleChassisNumber: vehicle.vehicleChassisNumber ?? '',
        }));
      },
      [],
    );

    const loadCustomerVehicles = useCallback(
      async (customerId: string) => {
        setVehicleLoading(true);
        setVehicleError(null);
        try {
          const data = await fetchCustomerVehicles({ customerId });
          setCustomerVehicles(data);
          if (data.length === 0) {
            setSelectedVehicleId('');
            setDetails((prev) => ({
              ...prev,
              customerVehicleId: '',
              vehicleBrandId: '',
              vehicleModelId: '',
              vehicleRegistrationYear: '',
              vehicleRegistrationNumber: '',
              vehicleVin: '',
              vehicleChassisNumber: '',
            }));
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unable to load vehicles.';
          setVehicleError(message);
          setCustomerVehicles([]);
          setSelectedVehicleId('');
          setDetails((prev) => ({
            ...prev,
            customerVehicleId: '',
            vehicleBrandId: '',
            vehicleModelId: '',
            vehicleRegistrationYear: '',
            vehicleRegistrationNumber: '',
            vehicleVin: '',
            vehicleChassisNumber: '',
          }));
        } finally {
          setVehicleLoading(false);
        }
      },
      [],
    );

    useEffect(() => {
      onChange?.({ ...details });
    }, [details, onChange]);

    useEffect(() => {
      const customerId = details.customer?.id;
      if (!customerId) {
        setCustomerVehicles([]);
        setSelectedVehicleId('');
        setVehicleError(null);
        setVehicleLoading(false);
        setDetails((prev) => ({
          ...prev,
          customerVehicleId: '',
          vehicleBrandId: '',
          vehicleModelId: '',
          vehicleRegistrationYear: '',
          vehicleRegistrationNumber: '',
          vehicleVin: '',
          vehicleChassisNumber: '',
        }));
        return;
      }

      loadCustomerVehicles(customerId);
    }, [details.customer?.id, loadCustomerVehicles]);

    const runSearch = useCallback(() => {
      const query = searchTerm.trim();

      if (!query) {
        setCustomerResults([]);
        setCustomerError('Enter a phone number or customer name before searching.');
        setHasSearched(false);
        setLastQuery(null);
        return;
      }

      setCustomerLoading(true);
      setCustomerError(null);
      setHasSearched(true);
      setShowResults(true);
      setLastQuery(query);

      fetchCustomers({ page: 1, limit: 10, search: query })
        .then((response) => {
          setCustomerResults(response.data);
        })
        .catch((error) => {
          setCustomerError(error instanceof Error ? error.message : 'Failed to load customers.');
        })
        .finally(() => {
          setCustomerLoading(false);
        });
    }, [searchTerm]);

    const handleSearchClick = useCallback(() => {
      runSearch();
    }, [runSearch]);

    const handleSearchKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          runSearch();
        }
      },
      [runSearch],
    );

    const handleSelectCustomer = useCallback((customer: Customer) => {
      setDetails(() => ({
        ...createEmptyCustomerVehicleSnapshot(),
        customer,
      }));
      setSelectedVehicleId('');
    }, []);

    const handleVehicleSelect = useCallback(
      (vehicleId: string) => {
        setSelectedVehicleId(vehicleId);
        const vehicle = customerVehicles.find((item) => item.id === vehicleId);
        if (vehicle) {
          applyVehicleToDetails(vehicle);
        } else {
          setDetails((prev) => ({
            ...prev,
            customerVehicleId: '',
            vehicleBrandId: '',
            vehicleModelId: '',
            vehicleRegistrationYear: '',
            vehicleRegistrationNumber: '',
            vehicleVin: '',
            vehicleChassisNumber: '',
          }));
        }
      },
      [applyVehicleToDetails, customerVehicles],
    );

    const handleOpenVehicleModal = useCallback(() => {
      if (!details.customer) {
        setCustomerError('Select a customer before adding a vehicle.');
        return;
      }
      const draft = createVehicleModalState();
      setVehicleModalState({
        vehicleBrandId: details.vehicleBrandId ?? draft.vehicleBrandId,
        vehicleModelId: details.vehicleModelId ?? draft.vehicleModelId,
        vehicleRegistrationYear: details.vehicleRegistrationYear ?? draft.vehicleRegistrationYear,
        vehicleRegistrationNumber: details.vehicleRegistrationNumber ?? draft.vehicleRegistrationNumber,
        vehicleVin: details.vehicleVin ?? draft.vehicleVin,
        vehicleChassisNumber: details.vehicleChassisNumber ?? draft.vehicleChassisNumber,
        fuelType: draft.fuelType,
      });
      setVehicleModalError(null);
      setVehicleModalSaving(false);
      setEditingVehicleId(null);
      setVehicleModalOpen(true);
    }, [createVehicleModalState, details.customer, details.vehicleBrandId, details.vehicleModelId, details.vehicleRegistrationNumber, details.vehicleRegistrationYear, details.vehicleVin, details.vehicleChassisNumber]);

    const handleVehicleModalChange = useCallback((field: VehicleModalField, value: string | string[]) => {
      setVehicleModalState((prev) => (prev ? { ...prev, [field]: value } : prev));
    }, []);

    const handleVehicleModalSubmit = useCallback(async () => {
      if (!details.customer || !vehicleModalState || vehicleModalSaving) {
        return;
      }

      const registrationNumber = vehicleModalState.vehicleRegistrationNumber.trim();
      if (!registrationNumber) {
        setVehicleModalError('Vehicle registration number is required.');
        return;
      }

      const payload = {
        customerId: details.customer.id,
        vehicleBrandId: vehicleModalState.vehicleBrandId || null,
        vehicleModelId: vehicleModalState.vehicleModelId || null,
        vehicleRegistrationYear: normalizeYearInput(vehicleModalState.vehicleRegistrationYear),
        vehicleRegistrationNumber: registrationNumber,
        vehicleVin: vehicleModalState.vehicleVin.trim() || null,
        vehicleChassisNumber: vehicleModalState.vehicleChassisNumber.trim() || null,
        fuelType: Array.isArray(vehicleModalState.fuelType)
          ? (vehicleModalState.fuelType.length > 0 ? vehicleModalState.fuelType.join(',') : null)
          : (vehicleModalState.fuelType || null),
        isActive: true,
      };

      setVehicleModalSaving(true);
      setVehicleModalError(null);

      try {
        const newVehicle = await createCustomerVehicle(payload);
        setCustomerVehicles((prev) => [newVehicle, ...prev.filter((item) => item.id !== newVehicle.id)]);
        setSelectedVehicleId(newVehicle.id);
        applyVehicleToDetails(newVehicle);
        setVehicleModalOpen(false);
        setVehicleModalState(null);
        setEditingVehicleId(null);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to save vehicle.';
        setVehicleModalError(message);
      } finally {
        setVehicleModalSaving(false);
      }
    }, [applyVehicleToDetails, details.customer, normalizeYearInput, vehicleModalSaving, vehicleModalState]);

    const handleOpenCustomerModal = useCallback(() => {
      setCustomerModalOpen(true);
      setSearchTerm('');
      setShowResults(false);
    }, []);

    const handleCustomerModalSubmit = useCallback(
      async (payload: { firstName: string; lastName: string; email: string; mobileNumber: string; dateOfBirth: string; countryCode?: string }) => {
        const customer = await createCustomer(payload);
        setDetails(() => ({
          ...createEmptyCustomerVehicleSnapshot(),
          customer,
        }));
        setCustomerResults((prev) => [customer, ...prev.filter((item) => item.id !== customer.id)]);
        setHasSearched(false);
        setSelectedVehicleId('');
        await loadCustomerVehicles(customer.id);
        setCustomerModalOpen(false);
      },
      [loadCustomerVehicles],
    );

    const validate = useCallback(() => {
      const errors: string[] = [];

      if (!details.customer) {
        errors.push('Select an existing customer to continue.');
      }

      if (!details.customerVehicleId) {
        errors.push('Select or add a vehicle to continue.');
      }

      // Require location selection when creating a new order
      if (!isEditingExistingOrder && !selectedLocationId) {
        errors.push('Select a location to continue.');
      }

      return errors;
    }, [details.customer, details.customerVehicleId, isEditingExistingOrder, selectedLocationId]);

    const setDetailsFromExternal = useCallback(
      (snapshot: CustomerVehicleSelectionSnapshot) => {
        setDetails(snapshot);
        setSelectedVehicleId(snapshot.customerVehicleId ?? '');
        if (snapshot.locationId !== undefined) {
          setSelectedLocationId(snapshot.locationId || '');
        }
        if (snapshot.customer) {
          setCustomerResults((existing) => {
            const exists = existing.some((customer) => customer.id === snapshot.customer?.id);
            return exists ? existing : [snapshot.customer as Customer, ...existing];
          });
          setHasSearched(true);
        }
      },
      [],
    );

    useImperativeHandle(
      ref,
      () => ({
        validate,
        getDetails: () => ({ ...details }),
        setDetails: setDetailsFromExternal,
      }),
      [details, setDetailsFromExternal, validate],
    );

    useEffect(() => {
      if (defaultSnapshot) {
        setDetailsFromExternal(defaultSnapshot);
        setFormErrors([]);
        setSubmitError(null);
      }

      if (defaultSnapshot === null) {
        setDetails(createEmptyCustomerVehicleSnapshot());
        setSelectedVehicleId('');
        setFormErrors([]);
        setSubmitError(null);
      }
    }, [defaultSnapshot, setDetailsFromExternal]);

    const customerSummaries = useMemo(
      () =>
        customerResults.map((customer) => {
          const name = `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim() || 'Unnamed customer';
          const contact = [customer.mobileNumber, customer.email]
            .filter(Boolean)
            .join(' · ') || 'No contact information';

          return {
            customer,
            name,
            contact,
          };
        }),
      [customerResults],
    );

    const helperMessage = useMemo(() => {
      if (customerError) {
        return { text: customerError, tone: 'error' } as const;
      }

      if (customerLoading) {
        return { text: 'Searching customers…', tone: 'info' } as const;
      }

      if (!hasSearched) {
        return {
          text: '',
          tone: 'info',
        } as const;
      }

      if (customerResults.length === 0) {
        return {
          text: lastQuery
            ? `No customers found for “${lastQuery}”. Try a different phone number or name.`
            : 'No customers found. Try a different phone number or name.',
          tone: 'info',
        } as const;
      }

      return {
        text: showResults
          ? 'Select the matching customer from the list below.'
          : '',
        tone: 'info',
      } as const;
    }, [customerError, customerLoading, hasSearched, customerResults.length, lastQuery, showResults]);

    const helperClassName = helperMessage.tone === 'error' ? 'text-danger' : 'text-slate-500';


    const hasFormErrors = formErrors.length > 0;
    const disableProceed = loadingUser || isProceeding;

    const handleProceed = useCallback(() => {
      const validationErrors = validate();
      setFormErrors(validationErrors);
      setSubmitError(null);

      if (validationErrors.length > 0) {
        return;
      }

      if (!currentUser) {
        setSubmitError('Your user profile is still loading. Please try again in a moment.');
        return;
      }

      const detailsSnapshot = { ...details };

      if (!detailsSnapshot.customer) {
        setSubmitError('Select an existing customer or create a new one to continue.');
        return;
      }

      if (!detailsSnapshot.customerVehicleId) {
        setSubmitError('Select a vehicle linked to this customer to continue.');
        return;
      }

      if (orderId) {
        navigate(`/order-management/new/vehicle-inspection?orderId=${orderId}`);
        return;
      }

      const payload: CreateOrderPayload = {
        customerId: detailsSnapshot.customer.id,
        customerVehicleId: detailsSnapshot.customerVehicleId,
        createdById: currentUser.id,
        status: 'draft',
        locationId: selectedLocationId,
      };

      setIsProceeding(true);

      createOrder(payload)
        .then((order) => {
          navigate(`/order-management/new/vehicle-inspection?orderId=${order.id}`);
        })
        .catch((error) => {
          setSubmitError(error instanceof Error ? error.message : 'Failed to create the order.');
        })
        .finally(() => {
          setIsProceeding(false);
        });
    }, [currentUser, details, navigate, orderId, validate]);

    const proceedButtonLabel = orderId ? 'Continue' : 'Save & continue';

    return (
      <section className="rounded-2xl border border-slate-200 bg-white p-2 shadow-sm mb-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Customer</h2>
            <p className="text-xs text-slate-500">
              {isEditingExistingOrder
                ? 'Customer and vehicle are locked for this order. Review the details below before continuing.'
                : 'Search by phone number to link an existing customer to this order.'}
            </p>
          </div>
        </div>

        <div className="mt-5 space-y-5">
          {!isEditingExistingOrder ? (
            <div className="space-y-3 mb-2">
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    if (customerError) {
                      setCustomerError(null);
                    }
                  }}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Enter phone number or customer name"
                  className="w-full border border-slate-300 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                <button
                  type="button"
                  className="inline-flex h-[40px] items-center justify-center bg-primary px-4 text-sm font-semibold text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-primary/60 sm:h-auto sm:py-2"
                  onClick={handleSearchClick}
                  disabled={customerLoading}
                >
                  {customerLoading ? 'Searching…' : 'Search'}
                </button>

                <button
                  type="button"
                  className="inline-flex h-[40px] items-center justify-center whitespace-nowrap border border-primary px-4 text-sm font-semibold text-primary transition hover:bg-primary/10 sm:h-auto sm:py-2"
                  onClick={handleOpenCustomerModal}
                >
                  + Add new customer
                </button>
              </div>

              {showResults ? (
                <div className="max-h-60 overflow-y-auto rounded-2xl bg-white shadow-inner">
                  {customerSummaries.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-slate-500">{helperMessage.text}</div>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {customerSummaries.map(({ customer, name, contact }) => (
                        <li key={customer.id}>
                          <button
                            type="button"
                            className="flex w-full flex-col items-start gap-1 px-4 py-3 text-left text-sm text-slate-600 transition hover:bg-primary/5 hover:text-primary"
                            onClick={() => {
                              handleSelectCustomer(customer);
                              setSearchTerm('');
                              setShowResults(false);
                            }}
                          >
                            <span className="font-semibold">{name}</span>
                            <span className="text-xs text-slate-500">{contact}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}

              {!showResults && helperMessage.text ? (
                <p className={`text-xs ${helperClassName}`}>{helperMessage.text}</p>
              ) : null}
            </div>
          ) : null}

          {details.customer && !isEditingExistingOrder ? (
            <div className="space-y-4">
              <div className="bg-slate-50/70 py-3 px-2 rounded">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex-1">
                    <p className="mb-2 text-sm font-medium text-slate-700">Select vehicle <span className="text-red-500">*</span></p>

                    {vehicleLoading ? (
                      <p className="text-xs text-slate-500">Loading vehicles…</p>
                    ) : customerVehicles.length === 0 ? (
                      <p className="text-xs text-slate-500">
                        No vehicles linked yet. Add one to reuse details.
                      </p>
                    ) : (
                      <div className="grid gap-3 sm:grid-cols-2">
                        {customerVehicles.map((vehicle) => {
                          const isSelected = selectedVehicleId === vehicle.id;
                          const labelParts = [
                            vehicle.vehicleRegistrationNumber ?? '—',
                            vehicle.vehicleRegistrationYear ?? undefined,
                          ]
                            .filter(Boolean)
                            .map((part) => String(part));

                          return (
                            <button
                              key={vehicle.id}
                              type="button"
                              onClick={() => handleVehicleSelect(vehicle.id ?? '')}
                              className={`flex w-full items-center rounded-sm border px-3 py-2 text-left text-sm transition ${isSelected
                                ? 'border-primary bg-primary/5 ring-2 ring-primary/40'
                                : 'border-slate-200 bg-white hover:border-primary/60 hover:bg-primary/5'
                                }`}
                            >
                              <span
                                className={`mr-3 inline-flex h-4 w-4 items-center justify-center rounded-full border ${isSelected ? 'border-primary bg-primary' : 'border-slate-300 bg-white'
                                  }`}
                              >
                                {isSelected ? (
                                  <span className="block h-2 w-2 rounded-full bg-white" />
                                ) : null}
                              </span>
                              <div className="flex flex-col">
                                <span className="font-medium text-slate-700">
                                  {labelParts.join(' • ')}
                                </span>
                                {!vehicle.isActive ? (
                                  <span className="text-[11px] font-medium uppercase tracking-wide text-amber-600">
                                    Inactive
                                  </span>
                                ) : null}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {vehicleError ? (
                      <p className="mt-2 text-xs text-danger">{vehicleError}</p>
                    ) : null}
                  </div>

                  <button
                    type="button"
                    onClick={handleOpenVehicleModal}
                    className="mt-2 inline-flex h-[48px] shrink-0 items-center justify-center whitespace-nowrap rounded-md border bg-primary px-6 text-sm font-medium text-white transition-colors hover:bg-primary/10 sm:mt-0 sm:ml-4"
                  >
                    + Add New Vehicle
                  </button>
                </div>
              </div>
              <div className=" bg-slate-50/70 py-3 px-2 rounded">
                <label className="block text-sm font-medium text-slate-700 mb-1">Select location <span className="text-red-500">*</span></label>
                <select
                  value={selectedLocationId}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedLocationId(value);
                    setDetails((prev) => ({
                      ...prev,
                      locationId: value,
                    }));
                  }}
                  className="w-full rounded-md border border-slate-300 px-4 py-3 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 sm:min-w-[260px]"
                  disabled={locationsLoading}
                >
                  <option value="">{locationsLoading ? 'Loading locations…' : 'Select a location'}</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>{loc.name}{loc.code ? ` (${loc.code})` : ''}</option>
                  ))}
                </select>
                {locationsError ? <p className="mt-1 text-xs text-danger">{locationsError}</p> : null}
              </div>
            </div>
          ) : null}

          {details.customer ? (
            <div className="rounded-2xl border border-emerald-300/60 bg-emerald-50/80 p-2">
              <dl className="grid gap-3 sm:grid-cols-2">
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">Name</dt>
                  <dd className="text-sm font-medium text-slate-700">
                    {`${details.customer.firstName ?? ''} ${details.customer.lastName ?? ''}`.trim() ||
                      'Unnamed customer'}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">Contact</dt>
                  <dd className="text-sm text-slate-700">{details.customer.mobileNumber ?? 'No mobile provided'}</dd>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">Email</dt>
                  <dd className="text-sm text-slate-700">{details.customer.email ?? 'No email provided'}</dd>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">Vehicle</dt>
                  <dd className="text-sm text-slate-700">
                    {(() => {
                      if (!details.customerVehicleId) {
                        return 'No vehicle selected.';
                      }
                      const vehicle = customerVehicles.find((item) => item.id === details.customerVehicleId);
                      if (!vehicle) {
                        const parts = [
                          details.vehicleRegistrationNumber || '—',
                          details.vehicleBrandId,
                          details.vehicleModelId,
                          details.vehicleRegistrationYear,
                        ]
                          .filter(Boolean)
                          .map((part) => String(part));
                        return parts.length ? parts.join(' • ') : 'Vehicle details unavailable.';
                      }
                      const parts = [
                        vehicle.vehicleRegistrationNumber ?? '—',
                        vehicle.vehicleBrand?.name,
                        vehicle.vehicleModel?.name,
                        vehicle.vehicleRegistrationYear ?? undefined,
                      ]
                        .filter(Boolean)
                        .map((part) => String(part));
                      return parts.join(' • ');
                    })()}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">Location</dt>
                  <dd className="text-sm text-slate-700">
                    {(() => {
                      const loc = locations.find((l) => l.id === selectedLocationId);
                      return loc ? `${loc.name}${loc.code ? ` (${loc.code})` : ''}` : '—';
                    })()}
                  </dd>
                </div>
              </dl>
            </div>
          ) : null}
        </div>

        <CustomerVehicleModal
          open={Boolean(vehicleModalOpen && vehicleModalState)}
          title={editingVehicleId ? 'Edit' : 'Add'}
          vehicleBrandId={vehicleModalState?.vehicleBrandId ?? ''}
          vehicleModelId={vehicleModalState?.vehicleModelId ?? ''}
          vehicleRegistrationYear={vehicleModalState?.vehicleRegistrationYear ?? ''}
          vehicleRegistrationNumber={vehicleModalState?.vehicleRegistrationNumber ?? ''}
          vehicleVin={vehicleModalState?.vehicleVin ?? ''}
          vehicleChassisNumber={vehicleModalState?.vehicleChassisNumber ?? ''}
          fuelType={Array.isArray(vehicleModalState?.fuelType) ? vehicleModalState.fuelType : []}
          onChange={handleVehicleModalChange}
          onClose={() => {
            setVehicleModalOpen(false);
            setVehicleModalState(null);
            setVehicleModalError(null);
            setVehicleModalSaving(false);
          }}
          onSubmit={handleVehicleModalSubmit}
          disabled={vehicleModalSaving}
          formError={vehicleModalError}
        />
        <AddCustomerModal
          open={customerModalOpen}
          onClose={() => {
            setCustomerModalOpen(false);
          }}
          onSubmit={handleCustomerModalSubmit}
        />

        {hasFormErrors ? (
          <div className="mt-4 rounded-xl border border-danger/40 bg-danger/10 p-4 text-sm text-danger">
            <p className="font-semibold">Please address the following:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {formErrors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {submitError ? (
          <div className="mt-4 rounded-xl border border-danger/40 bg-danger/10 p-4 text-sm text-danger">{submitError}</div>
        ) : null}

        {showProceedButton !== false && details.customer && details.customerVehicleId ? (
          <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              className="rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-primary/60"
              onClick={handleProceed}
              disabled={disableProceed}
            >
              {isProceeding ? 'Saving…' : proceedButtonLabel}
            </button>
          </div>
        ) : null}
      </section>
    );
  };

const CustomerVehicleSelection = forwardRef(CustomerVehicleSelectionComponent);

export default CustomerVehicleSelection;
